import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.tsx';
import { SessionProvider, TenantProvider } from './lib/contexts.tsx';
import { AdminApp } from './routes/admin/AdminApp.tsx';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A agenda muda enquanto o barbeiro está com o app aberto; voltar para o
      // app precisa mostrar o dia de agora, não o de vinte minutos atrás.
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      retry: 1,
    },
  },
});

// Só em produção: em desenvolvimento o service worker serviria a versão
// anterior do app e faria parecer que a alteração não pegou.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.warn('[pwa] service worker não registrou:', error);
    });
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('Elemento #root não encontrado');

/**
 * `admin.<domínio>` é o painel da plataforma, não de uma barbearia.
 *
 * O slug `admin` é reservado, então a API não resolve tenant nenhum ali — e o
 * `TenantProvider` trataria isso como "barbearia não encontrada". Por isso a
 * escolha acontece antes: o ramo do admin nem monta o provedor de tenant.
 */
const isAdminHost = window.location.hostname.split('.')[0] === 'admin';

if (isAdminHost) {
  // Sem barbearia não há cor de tenant: o painel usa o tema base, seguindo o
  // claro/escuro do sistema.
  document.documentElement.dataset.theme = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
  document.title = 'BarbearIA · plataforma';
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {isAdminHost ? (
          <SessionProvider>
            <AdminApp />
          </SessionProvider>
        ) : (
          <TenantProvider>
            <SessionProvider>
              <App />
            </SessionProvider>
          </TenantProvider>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
